/**
 * googleCalendarClient.ts
 * Todas as chamadas ao Google Calendar passam por aqui e vão para as
 * Cloud Functions — jamais direto à API do Google do frontend.
 */

import { auth } from "../firebase/client";

const BASE = "https://us-central1-orthoprecision-web.cloudfunctions.net";

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error("Usuário não autenticado.");
  return user.getIdToken();
}

async function post<T = unknown>(fn: string, body: Record<string, unknown>): Promise<T> {
  const token = await getIdToken();
  const res = await fetch(`${BASE}/${fn}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
  return data as T;
}

async function get<T = unknown>(fn: string): Promise<T> {
  const token = await getIdToken();
  const res = await fetch(`${BASE}/${fn}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
  return data as T;
}

// ─── Auth URL (popup OAuth) ───────────────────────────────────────────────────

export async function getGoogleAuthUrl(): Promise<string> {
  const data = await get<{ url: string }>("googleCalendarGetAuthUrl");
  return data.url;
}

/**
 * Abre um popup OAuth do Google e aguarda a resposta via postMessage.
 * Retorna o e-mail da conta conectada ou lança erro.
 */
export function openGoogleOAuthPopup(): Promise<string> {
  return new Promise(async (resolve, reject) => {
    let url: string;
    try { url = await getGoogleAuthUrl(); }
    catch (e) { reject(e); return; }

    const popup = window.open(
      url,
      "google-oauth-orthoprecision",
      "width=520,height=640,scrollbars=yes,resizable=yes"
    );

    if (!popup) {
      reject(new Error("Popup bloqueado. Permita pop-ups para este site."));
      return;
    }

    const timeout = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error("Tempo de resposta do Google expirou."));
    }, 120_000); // 2 min

    function handler(event: MessageEvent) {
      if (event.data?.type !== "GC_OAUTH") return;
      clearTimeout(timeout);
      window.removeEventListener("message", handler);
      if (event.data.status === "connected") resolve(event.data.email as string);
      else reject(new Error(event.data.error || "Autorização negada."));
    }

    window.addEventListener("message", handler);
  });
}

// ─── Status ───────────────────────────────────────────────────────────────────

export interface GoogleCalendarStatus {
  connected: boolean;
  email?: string;
  lastSyncAt?: number | null;
  syncError?: string | null;
}

export async function fetchGoogleCalendarStatus(): Promise<GoogleCalendarStatus> {
  return get<GoogleCalendarStatus>("googleCalendarStatus");
}

// ─── Disconnect ───────────────────────────────────────────────────────────────

export async function disconnectGoogleCalendar(): Promise<void> {
  await post("googleCalendarDisconnect", {});
}

// ─── Sync ─────────────────────────────────────────────────────────────────────

export async function syncGoogleCalendarNow(): Promise<{ synced: number }> {
  return post<{ synced: number }>("googleCalendarSyncManual", {});
}

// ─── Event operations (called after local save) ───────────────────────────────

export async function syncCreateEvent(params: {
  appointmentId: string;
  summary: string;
  description: string;
  location: string;
  start: number; // ms timestamp
  end: number;
}): Promise<string | null> {
  try {
    const res = await post<{ googleEventId: string }>("googleCalendarCreateEvent", {
      ...params,
      start: new Date(params.start).toISOString(),
      end:   new Date(params.end).toISOString(),
    });
    return res.googleEventId;
  } catch (err) {
    console.warn("[GC] syncCreateEvent failed (non-blocking):", err);
    return null;
  }
}

export async function syncUpdateEvent(params: {
  googleEventId: string;
  summary: string;
  description: string;
  location: string;
  start: number;
  end: number;
}): Promise<void> {
  try {
    await post("googleCalendarUpdateEvent", {
      ...params,
      start: new Date(params.start).toISOString(),
      end:   new Date(params.end).toISOString(),
    });
  } catch (err) {
    console.warn("[GC] syncUpdateEvent failed (non-blocking):", err);
  }
}

export async function syncDeleteEvent(googleEventId: string): Promise<void> {
  try {
    await post("googleCalendarDeleteEvent", { googleEventId });
  } catch (err) {
    console.warn("[GC] syncDeleteEvent failed (non-blocking):", err);
  }
}
